import { Button } from '@/components/ui/button';
import { Plus, Package, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Product {
  id: string;
  code: string;
  name: string;
  name_ar?: string;
  sale_price: number;
  purchase_price: number;
  vat: number;
  unit: string;
  stock_quantity: number;
  category?: string;
  image?: string;
  barcode?: string;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  layout?: 'grid' | 'list' | 'compact';
}

export const ProductCard = ({ product, onAddToCart, layout = 'grid' }: ProductCardProps) => {
  const formatPrice = (price: number) => {
    if (typeof price !== 'number' || isNaN(price)) {
      return '0.00 DA';
    }
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const isOutOfStock = product.stock_quantity <= 0;
  const isLowStock = product.stock_quantity > 0 && product.stock_quantity < 10;

  return (
    <div
      className={cn(
        'bg-white border-2 rounded-lg transition-all relative overflow-hidden',
        isOutOfStock
          ? 'border-red-200 bg-red-50/50 opacity-60'
          : 'border-gray-200 hover:border-emerald-400 hover:shadow-xl',
        layout === 'compact' && 'h-32',
        layout === 'grid' && 'h-full'
      )}
    >
      {/* Stock Alert Badge */}
      {isOutOfStock && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
            <AlertTriangle className="w-3 h-3" />
            نفذ / Épuisé
          </span>
        </div>
      )}
      {isLowStock && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
            <AlertTriangle className="w-3 h-3" />
            قليل / Faible
          </span>
        </div>
      )}

      <div className="p-4 h-full flex flex-col">
        {/* Product Image */}
        {product.image ? (
          <div className="w-full h-32 mb-3 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-gray-400"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
              }}
            />
          </div>
        ) : (
          <div className="w-full h-32 mb-3 rounded-lg bg-gray-100 flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-400" />
          </div>
        )}

        {/* Product Info */}
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">
            {product.name}
          </h3>
          {product.name_ar && (
            <p className="text-sm text-gray-500 line-clamp-1 mb-2">
              {product.name_ar}
            </p>
          )}
          <p className="text-xs text-gray-500 mb-2">Code: {product.code}</p>

          {/* Price & VAT */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-lg font-bold text-emerald-600">
              {formatPrice(product.sale_price)}
            </p>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
              TVA {product.vat}%
            </span>
          </div>

          {/* Stock */}
          <p
            className={cn(
              'text-xs font-medium mb-3',
              isOutOfStock ? 'text-red-600' : isLowStock ? 'text-yellow-600' : 'text-gray-600'
            )}
          >
            Stock: {product.stock_quantity} {product.unit}
          </p>
        </div>

        {/* Add Button */}
        <Button
          onClick={() => onAddToCart(product)}
          disabled={isOutOfStock}
          className={cn(
            'w-full h-12 gap-2 font-bold text-base transition-all',
            isOutOfStock
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 hover:scale-105'
          )}
        >
          <Plus className="w-5 h-5" />
          <span>إضافة / Ajouter</span>
        </Button>
      </div>
    </div>
  );
};
